'use client'

import {
  BlockquoteFigure,
  Document,
  Dropcursor,
  FontFamily,
  FontSize,
  Heading,
  HorizontalRule,
  Selection,
  SlashCommand,
  StarterKit,
  Subscript,
  Superscript,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TextAlign,
  UniqueID,
  TextStyle,
  Typography,
  Underline,
  History
} from '.'
import { isChangeOrigin } from '@tiptap/extension-collaboration'

interface ExtensionKitProps {
  provider?: null
}

import Paragraph from '@tiptap/extension-paragraph';
import ListItem from '@tiptap/extension-list-item';
import { Mark } from '@tiptap/core'
import { Node } from '@tiptap/core'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'

export const ExtensionKit = ({ provider, enableBold }: ExtensionKitProps) => [
  Document,
  History,
  Selection,
  Heading.configure({
    levels: [1, 2, 3, 4, 5, 6],
  }),
  UniqueID.configure({
    types: ['paragraph', 'heading', 'blockquote', 'codeBlock', 'table'],
    filterTransaction: transaction => !isChangeOrigin(transaction),
  }),
  HorizontalRule,
  StarterKit.configure({
    document: false,
    dropcursor: false,
    heading: false,
    horizontalRule: false,
    blockquote: false,
    history: false,
    codeBlock: false,
    paragraph: false,
    listItem: false,
    orderedList: false,
    bulletList: false,
    bold: enableBold,
  }),
  TextStyle,
  FontSize,
  FontFamily,
  Underline,
  TextAlign.extend({
    addKeyboardShortcuts() {
      return {}
    },
  }).configure({
    types: ['heading', 'paragraph'],
  }),
  Subscript,
  Superscript,
  Table,
  TableCell,
  TableHeader,
  TableRow,
  Typography,
  SlashCommand,
  BlockquoteFigure,
  Dropcursor.configure({
    width: 2,
    class: 'ProseMirror-dropcursor border-black',
  }),
  StyledParagraph,
  StyledListItem,
  StyledOrderedList,
  StyledBulletList,
  StyledStrong,
  StyledSpan,
]

export default ExtensionKit



const StyledParagraph = Paragraph.extend({
  addAttributes() {
    return {
      // Preserve all default attributes
      ...this.parent?.(),
      // Add the style attribute
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          return attributes.style ? { style: attributes.style } : {};
        },
      },
    };
  },
});

const StyledListItem = ListItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => {
          return attributes.style ? { style: attributes.style } : {};
        },
      },
    };
  },
});

const StyledOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
      },
    }
  },
})

const StyledBulletList = BulletList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
      },
    }
  },
})

const StyledSpan = Node.create({
  name: 'span',
  group: 'inline',
  inline: true,
  selectable: false,
  parseHTML() {
    return [{ tag: 'span' }]
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', HTMLAttributes, 0]
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
      },
    }
  },
})

const StyledStrong = Mark.create({
  name: 'strong',
  parseHTML() {
    return [
      { tag: 'strong' },
      { tag: 'b', getAttrs: node => node.style.fontWeight !== 'normal' && null },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['strong', HTMLAttributes, 0]
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: element => element.getAttribute('style'),
        renderHTML: attributes => (attributes.style ? { style: attributes.style } : {}),
      },
    }
  },
})